import { ExternalLink, HelpCircle, LayoutList, ListChecks, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { WhatsAppFormattedText } from '@/components/templates/WhatsAppFormattedText';
import { MediaMessage } from '@/components/media-message';
import { ContactsCard } from './contacts-card';
import { FlowReplyCard } from './flow-reply-card';
import { TemplateContent } from './template-content';
import type { Message } from './types';

const PLACEHOLDER_CONTENTS = new Set([
  '[Image attached]',
  '[Image]',
  '[Audio]',
  '[Video]',
  '[Document]',
  '[Sticker]',
  '[Interactive message]',
  '[Formulario completado]',
  '[Location request]',
  '[location message]',
  '[Flujo de WhatsApp]',
]);

function isPlaceholderContent(message: Message): boolean {
  if (!message.content) return true;
  if (PLACEHOLDER_CONTENTS.has(message.content)) return true;
  if (message.content.startsWith('[Location:')) return true;
  if (message.content.startsWith('[Contacto')) return true;
  return message.content === message.caption;
}

/**
 * A message with none of the dedicated affordances and placeholder-only content is
 * "unsupported": show a graceful muted chip instead of an empty bubble.
 */
function isUnsupported(message: Message): boolean {
  return (
    !message.hasMedia &&
    !message.metadata?.mediaId &&
    message.messageType !== 'location' &&
    !message.caption &&
    !message.interactiveButtons?.length &&
    !message.interactiveCtaButton &&
    !message.interactiveFlow &&
    !message.isLocationRequest &&
    !message.nfmReply &&
    !message.contacts?.length &&
    !message.template &&
    isPlaceholderContent(message)
  );
}

/** All per-type content blocks of a chat bubble (everything except the frame/footer). */
export function MessageContent({ message }: { message: Message }) {
  const { t } = useTranslation('conversations');

  const showText = !isPlaceholderContent(message);
  const unsupported = isUnsupported(message);

  return (
    <>
      {message.template && (
        <TemplateContent template={message.template} campaign={message.isCampaign} />
      )}

      {(message.hasMedia || message.metadata?.mediaId || message.messageType === 'location') &&
      message.messageType ? (
        <div className="mb-2">
          {message.messageType === 'sticker' && message.mediaData?.url ? (
            <img
              src={message.mediaData.url}
              alt={t('messageView.stickerAlt')}
              className="max-w-[150px] max-h-[150px] h-auto"
            />
          ) : (
            <MediaMessage
              mediaId={message.metadata?.mediaId}
              url={message.mediaData?.url}
              messageType={message.messageType}
              caption={message.caption}
              filename={message.filename}
              location={message.metadata?.location}
              isOutbound={message.direction === 'outbound'}
            />
          )}
        </div>
      ) : null}

      {message.caption && (
        <p className="text-sm break-words whitespace-pre-wrap mb-1">{message.caption}</p>
      )}

      {message.contacts && message.contacts.length > 0 && <ContactsCard contacts={message.contacts} />}

      {message.nfmReply && <FlowReplyCard nfmReply={message.nfmReply} />}

      {showText && (
        <p className="text-sm break-words whitespace-pre-wrap">
          <WhatsAppFormattedText
            text={
              // Strip the "[Btn1 | Btn2]" suffix added by extractMessageContent
              message.interactiveButtons || message.interactiveCtaButton
                ? message.content.replace(/\s*\[[^\]]*\]\s*$/, '')
                : message.content
            }
          />
        </p>
      )}

      {/* Incoming list-picker selection */}
      {message.listReply && (
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-chat-text-muted">
          <ListChecks className="h-3 w-3" />
          {t('messageView.listReplySelected')}
          {message.listReply.description ? ` · ${message.listReply.description}` : ''}
        </div>
      )}

      {/* Interactive Quick Reply buttons */}
      {message.interactiveButtons && message.interactiveButtons.length > 0 && (
        <div className="mt-2 border-t border-chat-border-soft pt-2 flex flex-col gap-1.5">
          {message.interactiveButtons.map((btn) => (
            <div
              key={btn.id}
              className="text-center text-sm font-medium text-chat-link py-1.5 rounded-md bg-chat-bubble-overlay border border-chat-border-soft"
            >
              {btn.title}
            </div>
          ))}
        </div>
      )}

      {/* Interactive CTA URL button */}
      {message.interactiveCtaButton && (
        <div className="mt-2 border-t border-chat-border-soft pt-2">
          <a
            href={message.interactiveCtaButton.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 text-sm font-medium text-chat-link py-1.5 rounded-md bg-chat-bubble-overlay border border-chat-border-soft hover:opacity-80 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {message.interactiveCtaButton.text}
          </a>
        </div>
      )}

      {/* WhatsApp Flow CTA (read-only affordance) */}
      {message.interactiveFlow && (
        <div className="mt-2 border-t border-chat-border-soft pt-2">
          <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-chat-link py-1.5 rounded-md bg-chat-bubble-overlay border border-chat-border-soft">
            <LayoutList className="h-3.5 w-3.5" />
            {message.interactiveFlow.ctaText || t('messageView.flowCta')}
          </div>
        </div>
      )}

      {/* Location request (read-only affordance) */}
      {message.isLocationRequest && (
        <div className="mt-2 border-t border-chat-border-soft pt-2">
          <div className="flex items-center justify-center gap-1.5 text-sm font-medium text-chat-link py-1.5 rounded-md bg-chat-bubble-overlay border border-chat-border-soft">
            <MapPin className="h-3.5 w-3.5" />
            {t('messageView.locationRequestCta')}
          </div>
        </div>
      )}

      {/* Graceful fallback for unknown/empty message kinds */}
      {unsupported && (
        <div className="flex items-center gap-1.5 text-xs text-chat-text-muted italic">
          <HelpCircle className="h-3.5 w-3.5" />
          {t('messageView.unsupportedType', { type: message.messageType || 'unknown' })}
        </div>
      )}
    </>
  );
}
